import type { QueryClient } from '@tanstack/react-query';
import { stripHtml } from '@worldcoo/shared/utils';
import { COUNTER_MODE_ATTR } from '@worldcoo/ui/lib/counter-contract';
import {
	isDocumentTranslated,
	isTimeZoneOptionIgnored,
	wasOurTextReplaced,
	type TextReplacement,
} from './frontend-error-filter';

/**
 * Records what the browser environment rewrites while React hydrates, so a mismatch can be attributed to a
 * translator, content blocker or WebView injection instead of to us; a log, because React's recovery restores
 * the text before the error is reported. Armed by an inline `<head>` script (the async module entry caught zero
 * of five rewrites on iOS) whose text must be byte-identical on server and client, or TanStack's dedup re-runs
 * the bootstrap. `childList` stays off: the measured wave rewrites in place, and watching every parsed node
 * costs the low-end devices that lose the race. docs/notes/portals/hydration-rewrite-monitor.md
 */
const REWRITE_LOG_KEY = '__wcTextRewrites';

/** Without it a capped log and a quiet page are the same silence, and `replacements=` reads as a total. */
const REWRITE_LOG_CAPPED_KEY = '__wcTextRewritesCapped';

/**
 * A record logged before it was raised predates any React DOM write, so it needs no witness. Only counted, not
 * gated on: hydration is concurrent and a rewrite can land mid-flight, so the next real ticket's `pre=` count
 * settles whether narrowing is safe.
 */
const HYDRATION_STARTED_KEY = '__wcHydrating';

/** Bounds a runaway or hostile script on a phone; one campaign page logged 27 entries against 5 rewrites. */
const MAX_RECORDS = 300;

/** Enough for a witness to still match on its prefix; short enough that a page-sized node cannot bloat the log. */
const MAX_VALUE_LENGTH = 512;

/**
 * `characterData` cannot see `#418 args[]=HTML`, which was every `pro` hydration report for 30 days (TEC-3881).
 * Reproduced: one hidden `div` injected into the footer newsletter's form before hydration raises it exactly.
 */
const NODE_LOG_KEY = '__wcNodeInserts';

const NODE_LOG_CAPPED_KEY = '__wcNodeInsertsCapped';

/**
 * React's own streamed-Suspense reveals: `$RV` moves a boundary's content out of its hidden `div#S:n` with
 * `insertBefore(…, <!--/$-->)`, deferred by up to 300 ms, so one can land before hydration. Counted apart, never
 * as an insertion, since they are ours (TEC-4023).
 */
const SUSPENSE_REVEALS_KEY = '__wcSuspenseReveals';

/** Room for an ancestor id or a two-step tag path; a page-sized id cannot bloat the log. */
const MAX_LOCATION_LENGTH = 48;

/** Hydration is over long before this; afterwards ordinary DOM churn must not be able to silence a report. */
const WATCH_WINDOW_MS = 10_000;

/**
 * Deliberately dumb, so the judgement lives in tested code ({@link isForeignTextReplacement}). The per-node id
 * `d` is its one addition: `nodeValue` is sampled at flush, and {@link recordedTextReplacements} needs node
 * identity to rebuild each record's true `after`.
 */
export const HYDRATION_REWRITE_MONITOR_SCRIPT =
	`(function(){try{` +
	`var K='${REWRITE_LOG_KEY}',L=window[K]=window[K]||[];` +
	`if(typeof MutationObserver==='undefined')return;` +
	`var M=new WeakMap(),n=0;` +
	`var o=new MutationObserver(function(rs){for(var i=0;i<rs.length;i++){` +
	`if(L.length>=${MAX_RECORDS}){window['${REWRITE_LOG_CAPPED_KEY}']=1;o.disconnect();return}` +
	`var r=rs[i];if(r.type!=='characterData')continue;var t=r.target;` +
	`var d=M.get(t);if(d==null)M.set(t,d=n++);` +
	`L.push([t.nodeType,(r.oldValue==null?'':r.oldValue).slice(0,${MAX_VALUE_LENGTH}),(t.nodeValue==null?'':t.nodeValue).slice(0,${MAX_VALUE_LENGTH}),window['${HYDRATION_STARTED_KEY}']?0:1,d,t.parentElement&&t.parentElement.closest('[${COUNTER_MODE_ATTR}]')?1:0])` +
	`}});` +
	`o.observe(document,{characterData:true,characterDataOldValue:true,subtree:true});` +
	`setTimeout(function(){try{o.disconnect()}catch(e){}},${WATCH_WINDOW_MS});` +
	// In `<head>`, not on `DOMContentLoaded`, which an injector's own listener wins; the `readyState` check
	// keeps `childList` free during parsing.
	`var J='${NODE_LOG_KEY}',I=window[J]=window[J]||[];` +
	// Where a node landed: the nearest ancestor with an id, else parent>tag (TEC-3956).
	`function W(e){for(var x=e.parentElement,k=0;x&&k<8;x=x.parentElement,k++){if(x.id)return('#'+x.id).slice(0,${MAX_LOCATION_LENGTH})}` +
	`var q=e.parentElement;return((q?q.tagName:'')+'>'+(e.tagName||'')).slice(0,${MAX_LOCATION_LENGTH})}` +
	`var p=new MutationObserver(function(rs){` +
	`if(document.readyState==='loading')return;` +
	`for(var i=0;i<rs.length;i++){` +
	`if(I.length>=${MAX_RECORDS}){window['${NODE_LOG_CAPPED_KEY}']=1;p.disconnect();return}` +
	`var ns=rs[i].nextSibling;if(ns&&ns.nodeType===8&&(ns.data==='/$'||ns.data==='/&')){window['${SUSPENSE_REVEALS_KEY}']=(window['${SUSPENSE_REVEALS_KEY}']||0)+1;continue}` +
	`var a=rs[i].addedNodes;for(var j=0;j<a.length;j++){var e=a[j];if(e.nodeType!==1)continue;` +
	`I.push([(e.tagName||'').slice(0,32),window['${HYDRATION_STARTED_KEY}']?0:1,e.closest&&e.closest('form')?1:0,W(e)])}` +
	`}});` +
	`p.observe(document,{childList:true,subtree:true});` +
	`setTimeout(function(){try{p.disconnect()}catch(e){}},${WATCH_WINDOW_MS})` +
	`}catch(e){}})();`;

const TEXT_NODE = 3;

/** Exported so its exclusions are unit-tested; the project has no DOM harness. */
export function isForeignTextReplacement(nodeType: number, before: string, after: string): boolean {
	// React's Suspense placeholders are comments rewritten in place: 22 on one page against 5 real rewrites.
	if (nodeType !== TEXT_NODE) return false;
	if (before === after) return false;
	// The parser appending a streamed text node: the document arriving, not being rewritten.
	if (after.startsWith(before)) return false;
	return true;
}

export interface RecordedTextReplacement extends TextReplacement {
	readonly preHydration: boolean;
	/** Inside a live donation counter, whose ordinary commits are not a server/client divergence (TEC-3998). */
	readonly insideCounter: boolean;
}

/** Called by the client entry just before `hydrateRoot`, and nowhere else. */
export function markHydrationStarted(): void {
	try {
		if (typeof window === 'undefined') return;
		(window as unknown as Record<string, unknown>)[HYDRATION_STARTED_KEY] = true;
	} catch {
		// A frozen `window` costs the phase breakdown in the report, nothing else.
	}
}

export interface RecordedNodeInsertion {
	readonly tagName: string;
	readonly preHydration: boolean;
	/** Inside a `<form>`, the population the injection was reproduced on (PR #2545). */
	readonly inForm: boolean;
	/** The nearest ancestor with an id, else `PARENT>TAG`; absent on tuples from an older recorder. */
	readonly location: string | undefined;
}

/**
 * Decisive, not heuristic: the recorder skips batches while `readyState` is `loading` (the parser) and stamps
 * the flag raised just before `hydrateRoot` (React), so nothing of ours writes in the remaining window. Scoped
 * to forms, the only place an injection was reproduced: an insertion anywhere else is reported (`domInserts=`)
 * but never suppresses a ticket, or a defect of ours could be blamed on an imaginary extension (TEC-3956).
 */
export function isForeignNodeInsertion(insertion: RecordedNodeInsertion): boolean {
	return insertion.preHydration && insertion.inForm;
}

/** Report-only: a pre-hydration insertion outside any form, the blind spot of TEC-3956's `inserts=0`. */
function isUnscopedNodeInsertion(insertion: RecordedNodeInsertion): boolean {
	return insertion.preHydration && !insertion.inForm;
}

export function recordedNodeInsertions(): readonly RecordedNodeInsertion[] {
	try {
		if (typeof window === 'undefined') return [];
		const raw = (window as unknown as Record<string, unknown>)[NODE_LOG_KEY];
		if (!Array.isArray(raw)) return [];
		return raw.flatMap((entry): RecordedNodeInsertion[] => {
			if (!Array.isArray(entry)) return [];
			const [tagName, preHydration, inForm, location] = entry as [unknown, unknown, unknown, unknown];
			if (typeof tagName !== 'string') return [];
			return [
				{
					tagName,
					preHydration: preHydration === 1,
					// An older recorder logged only nodes inside a form.
					inForm: inForm === undefined || inForm === 1,
					location: typeof location === 'string' ? location : undefined,
				},
			];
		});
	} catch {
		// An unreadable log costs the attribution, never the report.
		return [];
	}
}

function recordedSuspenseReveals(): number {
	try {
		if (typeof window === 'undefined') return 0;
		const count = (window as unknown as Record<string, unknown>)[SUSPENSE_REVEALS_KEY];
		return typeof count === 'number' ? count : 0;
	} catch {
		return 0;
	}
}

function wasNodeLogCapped(): boolean {
	try {
		if (typeof window === 'undefined') return false;
		return (window as unknown as Record<string, unknown>)[NODE_LOG_CAPPED_KEY] === 1;
	} catch {
		return false;
	}
}

export function recordedTextReplacements(): readonly RecordedTextReplacement[] {
	try {
		if (typeof window === 'undefined') return [];
		const raw = (window as unknown as Record<string, unknown>)[REWRITE_LOG_KEY];
		if (!Array.isArray(raw)) return [];
		const parsed: {
			nodeType: number;
			before: string;
			after: string;
			preHydration: boolean;
			nodeId: number | undefined;
			insideCounter: boolean;
		}[] = [];
		for (const entry of raw) {
			if (!Array.isArray(entry) || entry.length < 3) continue;
			const [nodeType, before, after, preHydration, nodeId, insideCounter] = entry as [
				unknown,
				unknown,
				unknown,
				unknown,
				unknown,
				unknown,
			];
			if (typeof nodeType !== 'number' || typeof before !== 'string' || typeof after !== 'string') continue;
			parsed.push({
				nodeType,
				before,
				after,
				preHydration: preHydration === 1,
				// Missing on tuples from an older recorder: the record survives, only un-chained below.
				nodeId: typeof nodeId === 'number' ? nodeId : undefined,
				// Missing on tuples from an older recorder: not a counter write.
				insideCounter: insideCounter === 1,
			});
		}
		// `nodeValue` is sampled at flush, so a record's true `after` is the next same-node `before`; without it a
		// rewrite React repairs in the same batch flattens to ours→ours and loses the translator's record.
		const nextBeforeByNode = new Map<number, string>();
		for (let i = parsed.length - 1; i >= 0; i--) {
			const record = parsed[i]!;
			if (record.nodeId === undefined) continue;
			const nextBefore = nextBeforeByNode.get(record.nodeId);
			if (nextBefore !== undefined) record.after = nextBefore;
			nextBeforeByNode.set(record.nodeId, record.before);
		}
		const replacements: RecordedTextReplacement[] = [];
		for (const { nodeType, before, after, preHydration, insideCounter } of parsed) {
			if (isForeignTextReplacement(nodeType, before, after)) {
				replacements.push({ before, after, preHydration, insideCounter });
			}
		}
		return replacements;
	} catch {
		return [];
	}
}

function wasRewriteLogCapped(): boolean {
	try {
		if (typeof window === 'undefined') return false;
		return (window as unknown as Record<string, unknown>)[REWRITE_LOG_CAPPED_KEY] === 1;
	} catch {
		return false;
	}
}

/**
 * Strings the server provably rendered from, out of the dehydrated cache. Enumerated rather than read by key,
 * because a rebuilt `getCampaign` key that drifted would silently disable the attribution. `stripHtml` because
 * the page renders both fields as DOM text.
 */
function renderedTextWitnesses(queryClient: QueryClient | undefined): string[] {
	try {
		const cache = queryClient?.getQueryCache();
		if (!cache) return [];
		const witnesses: string[] = [];
		for (const query of cache.getAll()) {
			const texts = (query.state.data as { texts?: { corporateTitle?: string; resumeShort?: string } } | undefined)
				?.texts;
			if (!texts) continue;
			if (texts.corporateTitle) witnesses.push(stripHtml(texts.corporateTitle));
			if (texts.resumeShort) witnesses.push(stripHtml(texts.resumeShort));
		}
		return witnesses;
	} catch {
		return [];
	}
}

/** Two pairs show a pattern without crowding the report; 80 characters is past where a diverging value shows. */
const MAX_NAMED_REPLACEMENTS = 2;
const MAX_NAMED_LENGTH = 80;

/**
 * The diff production React omits: #418's diff argument is a hard-coded empty string (the trailing `args[]=`).
 * Each pair is a text write seen during the watch window as before -> after, not necessarily React's repair of
 * server text. Truncation is marked so a clipped value never reads as a short one.
 */
export function describeTextMismatch(replacements: readonly RecordedTextReplacement[]): string | undefined {
	if (replacements.length === 0) return undefined;
	const clip = (value: string): string =>
		value.length > MAX_NAMED_LENGTH ? `${value.slice(0, MAX_NAMED_LENGTH)}…` : value;
	const named = replacements
		.slice(0, MAX_NAMED_REPLACEMENTS)
		.map(({ before, after }) => `${JSON.stringify(clip(before))} -> ${JSON.stringify(clip(after))}`);
	const rest = replacements.length - named.length;
	return rest > 0 ? `${named.join(' ; ')} (+${rest} more)` : named.join(' ; ');
}

export interface HydrationRewriteVerdict {
	/** Skip the report: the mismatch is the rewriter's. */
	readonly foreign: boolean;
	/** For the report's `counters` field; present even when nothing was found. */
	readonly attribution: string;
	/**
	 * Explains `args[]=text` only; absence is NOT a pristine page, since `args[]=HTML` leaves no record here
	 * (TEC-3881). docs/notes/portals/hydration-rewrite-monitor.md
	 */
	readonly mismatch: string | undefined;
}

/**
 * THE gate for hydration-class errors, and deliberately the only one: every report path asks this, so
 * they cannot drift apart. docs/notes/portals/hydration-rewrite-monitor.md
 */
export function describeHydrationRewrite(queryClient: QueryClient | undefined): HydrationRewriteVerdict {
	const witnesses = renderedTextWitnesses(queryClient);
	const replacements = recordedTextReplacements();
	const preHydration = replacements.filter((replacement) => replacement.preHydration).length;
	const insertions = recordedNodeInsertions();
	const foreignInserts = insertions.filter(isForeignNodeInsertion);
	const unscopedInserts = insertions.filter(isUnscopedNodeInsertion);
	const counterWrites = replacements.filter((replacement) => replacement.insideCounter).length;
	const firstUnscopedAt = unscopedInserts.find((insertion) => insertion.location)?.location;
	const timeZoneIgnored = isTimeZoneOptionIgnored();
	const reveals = recordedSuspenseReveals();
	return {
		foreign:
			isDocumentTranslated() ||
			timeZoneIgnored ||
			wasOurTextReplaced(replacements, witnesses) ||
			foreignInserts.length > 0,
		attribution:
			`rewriteCheck: witnesses=${witnesses.length} replacements=${replacements.length} pre=${preHydration} capped=${wasRewriteLogCapped() ? 1 : 0}` +
			` inserts=${foreignInserts.length}${wasNodeLogCapped() ? ' insertsCapped=1' : ''}` +
			` domInserts=${unscopedInserts.length}${firstUnscopedAt ? ` domInsertAt=${firstUnscopedAt}` : ''}` +
			(counterWrites > 0 ? ` counterWrites=${counterWrites}` : '') +
			(reveals > 0 ? ` reveals=${reveals}` : '') +
			(timeZoneIgnored ? ' tzIgnored=1' : ''),
		mismatch: describeTextMismatch(replacements.filter((replacement) => !replacement.insideCounter)),
	};
}
